import React from "react";
import {
  Card,
  Row,
  Col,
  Badge,
  Alert,
  Tooltip,
  OverlayTrigger,
} from "react-bootstrap";

// Detail Item Component
const DetailItem = ({ icon, label, value, color = "text-base", tooltip }) => {
  const tooltipElement = tooltip ? (
    <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
      <i
        className="fa fa-info-circle ml-1"
        style={{ opacity: 0.6, fontSize: "12px" }}
      ></i>
    </OverlayTrigger>
  ) : null;

  return (
    <Col xs={12} sm={6} md={4} className="mb-3">
      <div className="d-flex align-items-start">
        <div className="mr-2 mt-1 text-danger">
          <i className={`fa ${icon}`} style={{ fontSize: "14px" }}></i>
        </div>
        <div className="flex-grow-1">
          <div className="d-flex align-items-center mb-1">
            <small
              className="text-muted font-weight-bold text-uppercase"
              style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}
            >
              {label}
            </small>
            {tooltipElement}
          </div>
          <span
            className={`font-weight-bold ${color}`}
            style={{ fontSize: "0.875rem", wordBreak: "break-word" }}
          >
            {value || "—"}
          </span>
        </div>
      </div>
    </Col>
  );
};

// SectionCard Component
const SectionCard = ({ title, icon, children, count }) => {
  return (
    <Card className="card-primary border mb-3" style={{ borderRadius: "1rem" }}>
      <Card.Body className="p-4">
        <div className="d-flex flex-column gap-3">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div
                className="bg-danger rounded-circle d-flex align-items-center justify-content-center mr-3"
                style={{ width: "36px", height: "36px" }}
              >
                <i
                  className={`fa ${icon} text-white`}
                  style={{ fontSize: "14px" }}
                ></i>
              </div>
              <h6 className="font-weight-bold mb-0 color-dark">{title}</h6>
            </div>
            <div className="d-flex align-items-center">
              {count !== undefined && (
                <span
                  className="badge bg-danger text-white"
                  style={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    minWidth: "32px",
                    padding: "0.25rem 0.5rem",
                  }}
                >
                  {count}
                </span>
              )}
            </div>
          </div>
          {children}
        </div>
      </Card.Body>
    </Card>
  );
};

const UserDelete = ({ workQueueData }) => {
  const userData = workQueueData?.user;
  const workType = workQueueData?.workType || "DELETE USER";

  // User Header Component
  const UserHeader = () => (
    <div
      className="card card-primary border mb-4"
      style={{
        borderRadius: "1rem",
      }}
    >
      <div className="card-body p-4">
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center">
          <div
            className="bg-danger rounded-circle d-flex align-items-center justify-content-center mr-md-4 mb-3 mb-md-0"
            style={{ width: "64px", height: "64px" }}
          >
            <span
              className="text-white font-weight-bold"
              style={{ fontSize: "1.5rem" }}
            >
              {userData?.firstName?.[0]}
              {userData?.lastName?.[0]}
            </span>
          </div>
          <div className="flex-grow-1">
            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center mb-2">
              <h4 className="font-weight-bold mb-2 mb-sm-0 mr-3 color-dark">
                {`${userData?.firstName || ""} ${userData?.middleName || ""} ${
                  userData?.lastName || ""
                }`.trim()}
              </h4>
              <Badge variant="danger" className="d-flex align-items-center">
                <i className="fa fa-trash mr-1"></i>
                {workType}
              </Badge>
            </div>
            <div className="d-flex flex-column flex-sm-row flex-wrap align-items-start">
              <div className="d-flex align-items-center mb-1 mb-sm-0 mr-3">
                <i className="fa fa-envelope text-muted mr-1"></i>
                <span className="text-muted">{userData?.username}</span>
              </div>
              <div className="d-flex align-items-center mb-1 mb-sm-0 mr-3">
                <i className="fa fa-id-badge text-muted mr-1"></i>
                <span className="text-muted">{userData?.empCode}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-100">
      <UserHeader />

      {/* Main Content */}
      <Row>
        <Col xs={12}>
          <SectionCard title="User Information" icon="fa-user">
            <Row>
              <DetailItem
                icon="fa-envelope"
                label="Email"
                value={userData?.username}
                tooltip="Primary login identifier that will be deleted"
              />
              <DetailItem
                icon="fa-id-badge"
                label="Employee Code"
                value={userData?.empCode}
              />
              <DetailItem
                icon="fa-user"
                label="Full Name"
                value={`${userData?.firstName || ""} ${
                  userData?.middleName || ""
                } ${userData?.lastName || ""}`.trim()}
              />
              <DetailItem
                icon="fa-briefcase"
                label="Designation"
                value={userData?.designation}
              />
              <DetailItem
                icon="fa-phone"
                label="Mobile Number"
                value={userData?.mobileNo}
              />
              <DetailItem
                icon="fa-phone"
                label="Office Phone"
                value={userData?.officePhone}
              />
            </Row>
          </SectionCard>
        </Col>
      </Row>
      <Alert variant="warning" className="border-radius-3 mb-1 mt-1">
        <small className="font-weight-bold">
          This user account will be permanently deleted from the system. The
          user account remains active until this request is approved.
        </small>
      </Alert>
    </div>
  );
};

export default UserDelete;
